/**
 * System-wide role visibility (METALARK-685).
 *
 * Reads `hide_role.*` feature flags from the cached flag service to
 * determine which roles are available in the UI. Convention:
 *
 *   - `hide_role.RESOURCE_CONTRIBUTOR` flag active → role is hidden
 *   - No flag for a role → role is visible (default-enabled)
 *   - Delete all `hide_role.*` flags → every role is visible
 */

import { getFeatureFlagService } from '../services/service-loader';
import { locale } from '../services/localeService.js';

const HIDE_ROLE_PREFIX = 'hide_role.';

// Labels are resolved at call time via the canonical `admin_role_*` keys
// so this fallback stays in sync with the rest of the admin UI (no more
// "Tenant Admin" leaking in when the backend assignable-roles call fails
// and we fall back to this module).
const ROLE_VALUES = [
  'SYSTEM_ADMIN',
  'CUSTOMER_ADMIN',
  'TENANT_ADMIN',
  'RESOURCE_MANAGER',
  'TENANT_USER',
  'RESOURCE_CONTRIBUTOR',
  'TENANT_VIEWER',
];
// SERVICE_ROLE is excluded — internal service accounts only.
// SYSTEM_ADMIN is included; hierarchy checks limit who sees it.

function roleLabel(value) {
  const key = `admin_role_${value.toLowerCase().replace(/_/g, '')}`;
  const translated = locale?.t?.(key);
  if (translated && translated !== key) return translated;
  return value.split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');
}

function allAssignableRoles() {
  return ROLE_VALUES.map(value => ({ value, label: roleLabel(value) }));
}

/**
 * Get the list of roles currently enabled system-wide.
 * Reads from the feature flag cache (5-minute TTL) so this is
 * safe to call frequently without extra network requests.
 *
 * @returns {Promise<Array<{value: string, label: string}>>}
 */
export async function getEnabledRoles() {
  const all = allAssignableRoles();
  try {
    const flagService = await getFeatureFlagService();
    const flags = await flagService.getFlags();

    return all.filter(
      role => !flags[`${HIDE_ROLE_PREFIX}${role.value}`]
    );
  } catch {
    // Fail open — if flag service is unavailable, show all roles
    return all;
  }
}

/**
 * Synchronous version using cached flags only.
 * Returns all roles if flags haven't loaded yet (fail open).
 *
 * @returns {Array<{value: string, label: string}>}
 */
export function getEnabledRolesSync() {
  const all = allAssignableRoles();
  try {
    // Access the singleton's sync method directly — avoid the async import
    const { featureFlagService } = require('../api/featureFlagService.js');
    const flags = featureFlagService.getAllSync();

    return all.filter(
      role => !flags[`${HIDE_ROLE_PREFIX}${role.value}`]
    );
  } catch {
    return all;
  }
}
